import React from 'react';
import PropTypes from 'prop-types';
import { ToastContainer } from 'react-toastify';
import I18n from 'i18n-js';
import useSlideShowContext from '@/components/admin/SlideShow/stores/useSlideShowContext';
import Pill from '@/components/ui/Pill/Pill';
import { COLORS } from '@/components/ui/ui.constants';
import cn from '../../../../../utils/cn';
import { PRESENT_NAVIGATOR_ID, SLIDE_SETTINGS_STORAGE_KEY } from '../Constants';
import { TPM_CONTAINER_ID } from '../tpmToast';
import useFlags from '../../../../../hooks/useFlags';
import { autoplayMediaSetting, slidesHaveAutoplayAudioOrVideo } from '../Utils';
import styles from './Navigator.module.scss';
import { FullScreenPropTypes } from './Controls/Settings/FullScreen';
import AutoplayPopup from './AutoplayPopup';
import Navigation, { NavigationPropTypes } from './Controls/Navigation';
import Settings, { useSettings } from './Controls/Settings';
import { BackToButtonPropsTypes } from './Controls/BackToButton';
import { SlideShowEditButtonPropTypes } from './Controls/Settings/SlideShowEditButton';
import SlideStandards from './Controls/SlideStandards';
import SlideAudioControl, { SlideAudioControlMutedPropTypes } from './Controls/SlideAudioControl';
import LeftControl from './LeftControl';

const Navigator = ({
  BackToButtonProps, FullScreenProps, NavigationProps,
  SlideAudioControlProps, SlideShowEditButtonProps, slideShow,
}) => {
  const activeSessionId = useSlideShowContext(state => state.activeSessionId);
  const isSessionConnected = useSlideShowContext(state => state.isSessionConnected);
  const { settings, update, toggleAutoplayMedia } = useSettings(SLIDE_SETTINGS_STORAGE_KEY, { autoHide: false });
  const { accessibilityReflow, autoplayAudio } = useFlags();
  const slides = useSlideShowContext(state => state.slides);
  const hasUnsetAutoplaySetting = autoplayMediaSetting(slideShow.id) == null;
  const needsAutoplaySetting = autoplayAudio &&
                                 slidesHaveAutoplayAudioOrVideo(slides) &&
                                 hasUnsetAutoplaySetting;

  const setAutoplayMedia = (value) => {
    toggleAutoplayMedia(value, slideShow.id);
  };

  const showReconnectingPill = activeSessionId && isSessionConnected === false;

  return (
    <>
      <ToastContainer
        enableMultiContainer
        containerId={TPM_CONTAINER_ID}
        position="bottom-left"
        style={{ bottom: 58, zIndex: 10000 }}
      />
      <div className={`${styles.navigator} notranslate tw-font-tci`}>
        <div
          className={cn(styles.content, {
            [styles.autoHide]: settings.autoHide,
            [styles.liveSessionActive]: !!activeSessionId,
            [styles.reflow]: accessibilityReflow
          })}
          id={PRESENT_NAVIGATOR_ID}
        >
          <div className={styles.leftControlButtons}>
            <LeftControl BackToButtonProps={BackToButtonProps} />
          </div>
          <Navigation {...NavigationProps} />
          <div className={cn(styles.rightControlButtons, 'tw-flex tw-items-center tw-justify-self-end tw-gap-2')}>
            <SlideStandards />
            {autoplayAudio && (
              <SlideAudioControl
                {...SlideAudioControlProps}
                setVolumeInSettings={value => update('backgroundAudioVolume', value)}
                volumeFromSettings={settings.backgroundAudioVolume}
              />
            )}
            {needsAutoplaySetting && <AutoplayPopup setAutoplayMedia={setAutoplayMedia} />}
            {showReconnectingPill && (
              <Pill solid pillClassName="tw-text-xs tw-font-bold tw-uppercase tw-whitespace-nowrap !tw-text-tci-black" pillColor={COLORS.yellow}>
                <span className="tw-inline-block tw-w-2 tw-h-2 tw-rounded-full tw-bg-tci-black/60 tw-mr-1.5" />
                {I18n.t('teacher_paced_mode_reconnecting')}
              </Pill>
            )}
            <Settings
              fullScreenProps={FullScreenProps}
              settings={settings}
              slideShow={slideShow}
              slideShowEditButtonProps={SlideShowEditButtonProps}
              toggleAutoplayMediaEnabled={setAutoplayMedia}
              update={update}
            />
          </div>
        </div>
      </div>
    </>
  );
};

Navigator.propTypes = {
  BackToButtonProps: BackToButtonPropsTypes.isRequired,
  FullScreenProps: FullScreenPropTypes.isRequired,
  NavigationProps: NavigationPropTypes.isRequired,
  SlideAudioControlProps: SlideAudioControlMutedPropTypes.isRequired,
  SlideShowEditButtonProps: SlideShowEditButtonPropTypes.isRequired,
  slideShow: PropTypes.object.isRequired,
};

export default Navigator;
